/* SmartAuditLink — the One-Click Universal Metadata Importer.
 *
 * User pastes a MusicBrainz / Muso.ai / AllMusic / Genius URL,
 * hits "Run RoyAI Audit". Backend safely extracts metadata,
 * persists a track row, cross-checks Evidence Vault, and (if uncovered)
 * creates a Royalty Hunter Bounty.
 *
 * While the request is inflight we show a cinematic LCD scanning state.
 */
import { useEffect, useRef, useState } from "react";
import { Link2, Sparkles, CheckCircle2, AlertTriangle, ExternalLink, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { royai, fmtUsdCents } from "@/lib/royai";

const SUPPORTED = [
  { host: "musicbrainz.org", label: "MusicBrainz",   note: "API — highest fidelity" },
  { host: "muso.ai",         label: "Muso.AI",        note: "Credits + stream counts" },
  { host: "allmusic.com",    label: "AllMusic",       note: "Credits" },
  { host: "genius.com",      label: "Genius",         note: "Song metadata" },
];

const SCAN_LINES = [
  "Resolving source domain…",
  "Handshake with Music Infrastructure Grid…",
  "Fetching structured metadata (JSON-LD / OG)…",
  "Extracting title, artist, ISRC, credits…",
  "Cross-checking Evidence Vault™…",
  "Running Proactive Audit Engine…",
  "Estimating recoverable royalty value…",
  "Persisting to your Metadata Audit ledger…",
];

function ScanningOverlay() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 550);
    return () => clearInterval(id);
  }, []);
  const step = tick % SCAN_LINES.length;
  return (
    <div className="mt-4 border border-amber-500/30 bg-black/70 p-5 relative overflow-hidden" data-testid="importer-scanning">
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage:
          "repeating-linear-gradient(to bottom, rgba(245,200,66,0.04) 0px, rgba(245,200,66,0.04) 1px, transparent 1px, transparent 3px)",
        mixBlendMode: "overlay",
      }} />
      <div className="flex items-center gap-3 mb-3">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
        </span>
        <span className="font-mono-tech text-xs uppercase tracking-[0.28em] text-amber-400">
          Scanning Music Infrastructure…
        </span>
      </div>
      <div className="space-y-1 text-xs font-mono-tech text-neutral-300 relative">
        {SCAN_LINES.slice(0, step + 1).map((line, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className={i === step ? "text-amber-400" : "text-emerald-400"}>›</span>
            <span className={i === step ? "text-white" : "text-neutral-400"}>{line}</span>
            {i < step && <CheckCircle2 size={12} className="text-emerald-400 ml-auto" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function ResultCard({ result, onDismiss }) {
  const ex = result.extracted || {};
  return (
    <div className="mt-4 border border-emerald-500/40 bg-emerald-500/5 p-5" data-testid="importer-result">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono-tech uppercase tracking-widest text-emerald-300 mb-1">
            <Sparkles size={14} />
            {result.track_created ? "Track imported" : "Track refreshed"}
            <span className="text-neutral-500">·</span>
            <span className="text-neutral-400">{ex.provider}</span>
          </div>
          <div className="font-display text-xl text-white">{ex.title}</div>
          {ex.artist && <div className="text-sm text-neutral-400">{ex.artist}</div>}
        </div>
        {ex.source_url && (
          <a href={ex.source_url} target="_blank" rel="noreferrer"
             className="text-xs font-mono-tech uppercase tracking-widest text-amber-400 hover:text-amber-300 flex items-center gap-1"
             data-testid="importer-source-link">
            View source <ExternalLink size={12} />
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-4">
        <Field label="ISRC" value={ex.isrc || "—"} good={!!ex.isrc} />
        <Field label="ISWC" value={ex.iswc || "—"} good={!!ex.iswc} />
        <Field label="Label" value={ex.label || "—"} good={!!ex.label} />
        <Field label="Credits" value={(ex.credits || []).length ? `${ex.credits.length} found` : "—"} good={(ex.credits || []).length > 0} />
      </div>

      {ex.streams != null && (
        <div className="mb-3 text-xs font-mono-tech uppercase tracking-widest text-cyan-300">
          Streams (source): {new Intl.NumberFormat("en-US").format(ex.streams)}
        </div>
      )}

      {ex.low_fidelity && ex.extraction_note && (
        <div className="mb-3 text-[11px] border border-amber-500/30 bg-amber-500/5 text-amber-200 px-3 py-2" data-testid="importer-lowfi-note">
          {ex.extraction_note}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs pt-3 border-t border-white/5">
        {result.bounty_created ? (
          <span className="text-amber-400 flex items-center gap-1.5">
            <AlertTriangle size={12} /> New bounty — est. recovery {fmtUsdCents(result.bounty_estimated_usd)}
          </span>
        ) : result.covered_by_evidence ? (
          <span className="text-emerald-400 flex items-center gap-1.5">
            <CheckCircle2 size={12} /> Already covered by Evidence Vault
          </span>
        ) : (
          <span className="text-neutral-400">Bounty already tracked</span>
        )}
        <span className="text-neutral-500">·</span>
        <span className="text-neutral-300">
          Total recoverable now <span className="text-amber-400 font-mono-tech">{fmtUsdCents(result.total_recoverable_usd)}</span>
        </span>
        <button onClick={onDismiss}
          className="ml-auto text-xs font-mono-tech uppercase tracking-widest text-neutral-500 hover:text-neutral-300"
          data-testid="importer-clear">
          Import another
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, good }) {
  return (
    <div className="border border-white/5 bg-black/40 px-3 py-2">
      <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`text-sm truncate ${good ? "text-white" : "text-neutral-500"}`}>{value}</div>
    </div>
  );
}

export default function SmartAuditLink({ onAudited }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [shotBusy, setShotBusy] = useState(false);
  const [shotError, setShotError] = useState(null);
  const [shotResult, setShotResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  async function handleScreenshot(file) {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Upload a PNG, JPG, or WEBP screenshot.");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Image must be under 50 MB.");
      return;
    }
    setShotBusy(true); setShotError(null); setShotResult(null);
    try {
      const r = await royai.importScreenshot(file);
      setShotResult(r);
      const rows = r?.rows?.length || 0;
      toast.success(`RoyAI OCR'd ${rows} platform${rows === 1 ? "" : "s"} · Your share ${new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",maximumFractionDigits:0}).format(r?.totals?.your_share_usd || 0)}`);
      onAudited && await onAudited();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === "object" ? detail.message : (detail || "Screenshot OCR failed. Try a clearer image.");
      setShotError(message);
      toast.error(message);
    } finally {
      setShotBusy(false);
    }
  }

  async function submit(e) {
    e?.preventDefault?.();
    if (!url.trim()) { toast.error("Paste a MusicBrainz, Muso.ai, AllMusic, or Genius URL."); return; }
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await royai.importLink(url.trim());
      setResult(r);
      toast.success(`RoyAI audited "${r.extracted?.title}"`);
      onAudited && await onAudited();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === "object" ? detail.message : (detail || "Import failed. Try Advanced / Manual Entry.");
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-amber-500/25 bg-gradient-to-b from-amber-500/[0.04] to-black/40 p-5 lg:p-7 mb-4"
         data-testid="smart-audit-link">
      <div className="flex items-center gap-3 mb-2">
        <Link2 size={18} className="text-amber-400" />
        <div>
          <h3 className="font-display text-lg lg:text-xl font-medium text-white">Smart Audit Link</h3>
          <div className="text-xs font-mono-tech uppercase tracking-widest text-amber-400/80">
            One click · Universal Metadata Importer
          </div>
        </div>
      </div>
      <p className="text-sm text-neutral-400 mb-4 max-w-3xl">
        Paste a MusicBrainz, Muso.AI, AllMusic, or Genius URL. RoyAI safely extracts title, artist, ISRC,
        credits, and (where available) stream counts — then runs the Proactive Audit Engine to generate
        royalty bounties automatically.
      </p>

      <form onSubmit={submit} className="flex flex-col md:flex-row gap-3">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          disabled={busy}
          placeholder="https://musicbrainz.org/recording/…   or   https://muso.ai/artist/…"
          className="flex-1 bg-black border border-white/10 focus:border-amber-500 outline-none px-4 py-3 text-sm md:text-base text-white placeholder:text-neutral-600 font-mono-tech"
          data-testid="smart-audit-link-input"
        />
        <button
          type="submit"
          disabled={busy}
          className="bg-amber-500 text-black px-6 py-3 text-sm font-mono-tech uppercase tracking-[0.2em] font-semibold hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shrink-0"
          data-testid="smart-audit-link-submit"
        >
          <Sparkles size={16} /> {busy ? "Auditing…" : "Run RoyAI Audit"}
        </button>
      </form>

      <div className="flex flex-wrap gap-2 mt-3">
        {SUPPORTED.map((s) => (
          <span key={s.host}
            className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 border border-white/10 bg-black/60 px-2 py-1"
            title={s.note}>
            {s.label}
          </span>
        ))}
      </div>

      {busy && <ScanningOverlay />}

      {error && !busy && (
        <div className="mt-4 border border-rose-500/40 bg-rose-500/5 p-4 text-sm text-rose-200 flex items-start gap-2"
             data-testid="importer-error">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium mb-0.5">Couldn&apos;t audit that link</div>
            <div className="text-xs text-rose-300/80">{error}</div>
            <div className="text-xs text-neutral-500 mt-2">
              You can still add this track using <span className="text-amber-400">Advanced / Manual Entry</span> below.
            </div>
          </div>
        </div>
      )}

      {result && !busy && (
        <ResultCard result={result} onDismiss={() => { setResult(null); setUrl(""); }} />
      )}

      {/* Screenshot uploader — drop your Muso.AI / Spotify-for-Artists chart */}
      <div className="mt-6 pt-6 border-t border-white/10">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="flex items-center gap-2">
            <ImageIcon size={16} className="text-amber-400" />
            <h4 className="font-display text-base font-medium text-white">Or drop an analytics screenshot</h4>
          </div>
          <span className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">
            Muso.AI · Spotify for Artists · Chartmetric · Songstats
          </span>
        </div>
        <p className="text-xs text-neutral-400 mb-3 max-w-3xl">
          Screenshot your streams chart with the per-platform legend visible. RoyAI reads the numbers with Gemini vision and builds a Deep Dive Revenue Report from the <span className="text-amber-400">actual</span> streams — no estimates.
        </p>

        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragOver(false);
            const f = e.dataTransfer?.files?.[0];
            if (f) handleScreenshot(f);
          }}
          className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed px-6 py-8 cursor-pointer transition-colors text-center
            ${shotBusy ? "border-amber-500/60 bg-amber-500/10 cursor-wait"
                       : dragOver ? "border-amber-400 bg-amber-500/10"
                                  : "border-white/15 bg-black/40 hover:border-amber-500/60 hover:bg-amber-500/[0.04]"}`}
          data-testid="screenshot-dropzone"
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={shotBusy}
            onChange={(e) => handleScreenshot(e.target.files?.[0])}
            className="hidden"
            data-testid="screenshot-input"
          />
          <Upload size={22} className={shotBusy ? "text-amber-400 animate-pulse" : "text-neutral-400"} />
          <div className="text-sm text-white">
            {shotBusy ? "OCR in progress…" : "Drop screenshot here, or click to upload"}
          </div>
          <div className="text-[11px] text-neutral-500">PNG · JPG · WEBP · up to 10 MB</div>
        </label>

        {shotBusy && <ScanningOverlay />}

        {shotError && !shotBusy && (
          <div className="mt-4 border border-rose-500/40 bg-rose-500/5 p-4 text-sm text-rose-200 flex items-start gap-2"
               data-testid="screenshot-error">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-medium mb-0.5">Couldn&apos;t read that screenshot</div>
              <div className="text-xs text-rose-300/80">{shotError}</div>
            </div>
          </div>
        )}

        {shotResult && !shotBusy && (
          <ScreenshotResultCard result={shotResult} onDismiss={() => setShotResult(null)} />
        )}
      </div>
    </div>
  );
}

function ScreenshotResultCard({ result, onDismiss }) {
  const ex = result.extracted || {};
  const rows = result.rows || [];
  const totals = result.totals || {};
  const nfInt = new Intl.NumberFormat("en-US");
  return (
    <div className="mt-4 border border-emerald-500/40 bg-emerald-500/5 p-5" data-testid="screenshot-result">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono-tech uppercase tracking-widest text-emerald-300 mb-1">
            <Sparkles size={14} /> OCR ingested
            {ex.source_hint && <span className="text-neutral-500">· {ex.source_hint}</span>}
            {ex.date_range && <span className="text-neutral-500">· {ex.date_range}</span>}
          </div>
          <div className="font-display text-xl text-white">{ex.track_title || ex.artist || "Screenshot Import"}</div>
          {ex.artist && ex.track_title && <div className="text-sm text-neutral-400">{ex.artist}</div>}
          {ex.total_streams != null && (
            <div className="text-xs text-neutral-500 mt-1">Total streams read: {nfInt.format(ex.total_streams)}</div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">Your Share</div>
          <div className="font-display text-2xl text-amber-400">{fmtUsdCents(totals.your_share_usd || 0)}</div>
          <div className="text-[10px] text-neutral-500">of {fmtUsdCents(totals.total_revenue_usd || 0)}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between border border-white/5 bg-black/40 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: r.platform_color }} />
              <span className="text-sm text-white truncate">{r.platform}</span>
              <span className="text-[10px] font-mono-tech text-neutral-500 ml-1">{nfInt.format(r.streams)} streams</span>
            </div>
            <span className="text-sm font-mono-tech text-amber-400">{fmtUsdCents(r.user_share_usd)}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-white/5 text-xs">
        <span className="text-neutral-400">
          {result.bounties_created} new track-specific bount{result.bounties_created === 1 ? "y" : "ies"} added to the Hunter.
        </span>
        <button onClick={onDismiss} className="font-mono-tech uppercase tracking-widest text-neutral-500 hover:text-neutral-300"
                data-testid="screenshot-clear">
          Upload another
        </button>
      </div>
    </div>
  );
}
